<template>
  <NotificationsWrapper>
    <div slot-scope="{ onNotificationClick }">
      <div class="notifications-alert-container px-4 pb-4">
        <div class="d-flex justify-end mb-2">
          <Button
            v-if="alertsNotifications.length > 1"
            color="white"
            elevation="2"
            size="small"
            :text="$t('common.clear_all')"
            @click="clearAllNotifications"
          />
        </div>
        <transition-group name="slide-fade" tag="div">
          <v-alert
            v-for="(notification, index) in alertsNotifications"
            border="left"
            class="cursor-pointer pb-4 pt-2 pe-2 ps-6"
            colored-border
            elevation="2"
            value
            :color="getColorByNotificationType(notification.type)"
            :key="notification._id || index"
            @click="handleNotificationClick(onNotificationClick, notification)"
          >
            <div class="mt-2 ms-2">
              <p
                class="ma-0 pa-0 text-body-2 blue-grey--text text--darken-3"
                v-html="
                  $sanitize(getMessage(notification), {
                    allowedTags: ['a', 'b', 'br'],
                  })
                "
              />

              <p
                class="mb-0 mt-2 pa-0 text-caption blue-grey--text"
                v-if="notification.createdAt"
              >
                {{ getDate(notification) }}
              </p>
            </div>

            <template v-slot:append>
              <div class="align-self-start ms-4 mt-1">
                <v-icon
                  color="blue-grey lighten-1"
                  size="24"
                  @click.stop="dismissNotification(notification)"
                  >mdi-close-circle</v-icon
                >
              </div>
            </template>
          </v-alert>
        </transition-group>
      </div>
    </div>
  </NotificationsWrapper>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'

import NotificationsWrapper from '@/components/Notifications/NotificationsWrapper.vue'
import Button from '@/components/UI/Button.vue'

import access from '@/lib/access'
import mixin from '@/mixins/mixin.vue'
import { utils } from 'frontend-js-shared'

const THIRTY_SECONDS = 30000
const FIVE_SECONDS = 5000
const NOTIFICATIONS_LIMIT = 10

export default {
  components: {
    NotificationsWrapper,
    Button,
  },

  mixins: [mixin],

  data() {
    return {
      intervalForNotifications: null,
      notificationsDismissTimeouts: [],
    }
  },

  mounted() {
    this.startPolling()
    this.scheduleNotificationsDismiss(this.alertsNotifications)
  },

  watch: {
    alertsNotifications(notifications = []) {
      this.scheduleNotificationsDismiss(notifications)
    },
  },

  computed: {
    ...mapGetters(['hasUnReadNotifications']),
    ...mapState({
      alertsNotifications(state) {
        return (
          state.notifications?.alertsNotifications?.filter(
            notification => !notification.read
          ) ?? []
        )
      },
    }),
  },

  methods: {
    ...mapActions([
      'dismissNotification',
      'pollNotifications',
      'getNotifications',
    ]),

    handleNotificationClick(onNotificationClick, notification) {
      onNotificationClick(notification)
      this.dismissNotification(notification)
    },

    clearAllNotifications() {
      this.dismissAllNotifications()
    },

    getMessage(notification) {
      return notification?.message ?? ''
    },

    getDate(notification) {
      return utils.dates.humanDate(notification.createdAt)
    },

    dismissAllNotifications() {
      this.alertsNotifications.forEach(this.dismissNotification)
    },

    clearNotificationsDismissTimeouts() {
      this.notificationsDismissTimeouts.forEach(timeoutId => {
        clearTimeout(timeoutId)
      })

      this.notificationsDismissTimeouts = []
    },

    startPolling() {
      this.getNotifications()

      this.intervalForNotifications = setInterval(
        () => this.getNotificationsStatus(),
        THIRTY_SECONDS
      )
    },

    getNotificationsStatus() {
      if (!this.isUserLoggedIn()) {
        return clearInterval(this.intervalForNotifications)
      }

      this.pollNotifications({
        localStorage: window.localStorage,
        limit: NOTIFICATIONS_LIMIT,
      }).catch(error => {
        console.log(error)
      })
    },

    isUserLoggedIn() {
      return access.isLoggedIn()
    },

    getColorByNotificationType(type) {
      switch (type) {
        case 'success':
          return '#20c933'
        case 'warning':
          return '#fcb400'
        case 'danger':
          return 'red'
        default:
          return '#3882f0'
      }
    },

    scheduleNotificationsDismiss(notifications = []) {
      this.clearNotificationsDismissTimeouts()

      const unreadNotifications = notifications.filter(
        notification => !notification.read
      )

      unreadNotifications.forEach(notification => {
        const timeoutId = setTimeout(() => {
          this.dismissNotification(notification)
        }, FIVE_SECONDS)

        this.notificationsDismissTimeouts.push(timeoutId)
      })
    },
  },

  beforeDestroy() {
    clearInterval(this.intervalForNotifications)
    this.clearNotificationsDismissTimeouts()
  },
}
</script>

<style lang="scss" scoped>
.notifications-alert-container {
  position: fixed;
  top: 64px;
  right: 0;
  max-width: 350px;
  z-index: 99999999;
  height: auto;
}
</style>
